import React, { useState } from 'react';
import useToastContext from 'hooks/useToastContext';
import { useForm } from 'react-hook-form';
import { apiPost, apiPut, apiDelete } from '../../utils/apiFetch';
import apiPath from '../../utils/apiPath';
import { useTranslation } from 'react-i18next';
import OInputField from 'components/reusable/OInputField';
import { isEmpty } from 'lodash';
import ConfirmationModal from '../../components/reusable/ConfirmationModal';
import OTextArea from 'components/reusable/OTextArea';
import { Switch } from '@headlessui/react';
import { AiFillEdit, AiFillDelete, AiOutlinePlus } from "react-icons/ai";
import dayjs from "dayjs";

const BlogCategory = ({ categories, getCategories, manager, user, page, isLoading }) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null });
  const [categoryModal, setCategoryModal] = useState({ isOpen: false, mode: 'add' }); // 'add' or 'edit'
  const [loadingStates, setLoadingStates] = useState({});

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  const openAddModal = () => {
    setEditingCategory(null);
    reset({
      name: '',
      description: ''
    });
    setCategoryModal({ isOpen: true, mode: 'add' });
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    reset({
      name: category.name,
      description: category.description
    });
    setCategoryModal({ isOpen: true, mode: 'edit' });
  };

  const closeCategoryModal = () => {
    setCategoryModal({ isOpen: false, mode: 'add' });
    setEditingCategory(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      setLoadingStates(prev => ({ ...prev, form: true }));
      let result;
      
      if (editingCategory) {
        result = await apiPut(`${apiPath.blogCategory}/${editingCategory._id}`, data);
      } else {
        result = await apiPost(apiPath.blogCategory, data);
      }

      if (result?.data?.success) {
        notification.success(result.data.message);
        closeCategoryModal();
        getCategories();
      } else {
        notification.error(result?.data?.message);
      }
    } catch (error) {
      console.log('Error:', error);
      notification.error(error.message);
    } finally {
      setLoadingStates(prev => ({ ...prev, form: false }));
    }
  };

  const handleDelete = async (item) => {
    try {
      setLoadingStates(prev => ({ ...prev, delete: true }));
      const result = await apiDelete(`${apiPath.blogCategory}/${item._id}`);
      
      if (result?.data?.success) {
        notification.success(result.data.message);
        getCategories();
      } else {
        notification.error(result?.data?.message);
      }
    } catch (error) {
      console.log('Error:', error);
      notification.error(error.message);
    } finally {
      setLoadingStates(prev => ({ ...prev, delete: false }));
      setDeleteModal({ isOpen: false, item: null });
    }
  };

  const handleStatusChange = async (category) => {
    try {
      setLoadingStates(prev => ({ ...prev, [category._id]: true }));

      const payload = {
        status: category.status === 'active' ? 'inactive' : 'active'
      };
      const result = await apiPut(`${apiPath.changeBlogCategoryStatus}/${category._id}`, payload);
      
      if (result?.data?.success) {
        notification.success(result.data.message);
        getCategories();
      } else {
        notification.error(result?.data?.message);
      }
    } catch (error) {
      console.log('Error:', error);
      notification.error(error.message);
    } finally {
      setLoadingStates(prev => ({ ...prev, [category._id]: false }));
    }
  };

  return (
    <div className="p-5">
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, item: null })}
        onConfirm={() => handleDelete(deleteModal.item)}
        title={t("CONFIRM_DELETE")}
        message={t("ARE_YOU_SURE_DELETE_THIS_CATEGORY", { name: deleteModal.item?.name })}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        isLoading={loadingStates.delete}
        isDanger={true}
      />

      {/* Add/Edit Category Modal */}
      {categoryModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                {categoryModal.mode === 'add' ? t('ADD_CATEGORY') : t('EDIT_CATEGORY')}
              </h3>
              <button
                onClick={closeCategoryModal}
                className="text-gray-500 hover:text-gray-700 transition duration-150"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="p-6">
              <div className="space-y-4">
                <OInputField
                  wrapperClassName="relative z-0 mb-4 w-full group"
                  name="name"
                  inputLabel={
                    <>
                      {t('CATEGORY_NAME')}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  type="text"
                  register={register('name', { 
                    required: 'Category name is required',
                    minLength: { value: 2, message: 'Category name must be at least 2 characters' }
                  })}
                  errors={errors}
                />

                <OTextArea
                  wrapperClassName="relative z-0 mb-4 w-full group"
                  name="description"
                  inputLabel={<>{t('DESCRIPTION')}</>}
                  type="text"
                  register={register('description')}
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeCategoryModal}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition duration-150"
                >
                  {t('CANCEL')}
                </button>
                <button
                  type="submit"
                  disabled={loadingStates.form}
                  className="px-4 py-2 bg-LightBlue text-white rounded-lg hover:bg-DarkBlue transition duration-150 disabled:opacity-50"
                >
                  {loadingStates.form ? t('SAVING') : (categoryModal.mode === 'add' ? t('O_ADD') : t('O_UPDATE'))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Categories Table Section */}
      <div className="bg-white border border-[#E9EDF9] rounded-lg">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">{t('CATEGORIES')}</h3>
          {(user?.role === "admin" || manager?.add) && (
            <button
              onClick={openAddModal}
              className="bg-LightBlue text-white px-4 py-2 rounded-lg hover:bg-DarkBlue transition duration-150 flex items-center space-x-2"
            >
              <AiOutlinePlus className="w-4 h-4" />
              <span>{t('ADD_CATEGORY')}</span>
            </button>
          )}
        </div>
        
        <div className="p-3">
          <div className="overflow-x-auto relative rounded-lg border">
            <table className="w-full text-xs text-left text-[#6c6c6c] dark:text-gray-400">
              <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="py-3 px-6">
                    {t("SR_NO")}
                  </th>
                  <th scope="col" className="py-3 px-6">
                    {t("CATEGORY_NAME")}
                  </th>
                  <th scope="col" className="py-3 px-6">
                    {t("DESCRIPTION")}
                  </th>
                  <th scope="col" className="py-3 px-6">
                    {t("CREATED_DATE")}
                  </th>
                  <th scope="col" className="py-3 px-6">
                    {t("STATUS")}
                  </th>
                  <th scope="col" className="py-3 px-6 text-center">
                    {t("ACTIONS")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="py-4 px-6 text-center">
                      {t("LOADING")}...
                    </td>
                  </tr>
                ) : categories?.length > 0 ? (
                  categories.map((category, i) => (
                    <tr
                      key={category._id}
                      className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                    >
                      <td className="py-4 px-6 border-r font-medium text-gray-900">
                        {i + 1 + 10 * (page - 1)}
                      </td>
                      <td className="py-4 px-6 border-r text-gray-900">
                        {category?.name}
                      </td>
                      <td className="py-4 px-6 border-r text-gray-900">
                        {category?.description || 'N/A'}
                      </td>
                      <td className="py-4 px-6 border-r text-gray-900">
                        {dayjs(category?.createdAt).format("MM-DD-YYYY h:mm A")}
                      </td>
                      <td className="py-4 px-6 border-r">
                        <div className="flex items-center">
                          <Switch
                            checked={category.status === "active"}
                            onChange={() => handleStatusChange(category)}
                            disabled={loadingStates[category._id] || !(user?.role === "admin" || manager?.status)}
                            className={`${category.status === "active" ? "bg-LightBlue" : "bg-gray-200"
                              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-LightBlue ${loadingStates[category._id] ? "opacity-50 cursor-not-allowed" : ""
                              }`}
                          >
                            <span
                              className={`${category.status === "active" ? "translate-x-6" : "translate-x-1"
                                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                            />
                          </Switch>
                        </div>
                      </td>
                      <td className="text-gray-900 py-4 px-6">
                        <div className="flex justify-center space-x-3">
                          {(user?.role === "admin" || manager?.edit) && (
                            <button
                              onClick={() => openEditModal(category)}
                              title={t("EDIT")}
                              className="text-slate-600 hover:text-indigo-700 transition duration-150"
                            >
                              <AiFillEdit className="w-5 h-5" />
                            </button>
                          )}
                          {(user?.role === "admin" || manager?.delete) && (
                            <button
                              onClick={() => {
                                if (!category?._id) {
                                  notification.error("Invalid category ID");
                                  return;
                                }
                                setDeleteModal({
                                  isOpen: true,
                                  item: category
                                });
                              }}
                              title={t("DELETE")}
                              className="text-slate-600 hover:text-red-600 transition duration-150"
                            >
                              <AiFillDelete className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="bg-white border-b text-center">
                    <td className="py-4 px-6" colSpan={6}>
                      {t("NO_CATEGORIES_FOUND")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogCategory;



// import React, { useState } from 'react';
// import useToastContext from 'hooks/useToastContext';
// import { useForm } from 'react-hook-form';
// import { apiPost, apiPut, apiDelete } from '../../utils/apiFetch';
// import apiPath from '../../utils/apiPath';
// import { useTranslation } from 'react-i18next';
// import OInputField from 'components/reusable/OInputField';
// import { isEmpty } from 'lodash';
// import ConfirmationModal from '../../components/reusable/ConfirmationModal';
// import OTextArea from 'components/reusable/OTextArea';
// import { Switch } from '@headlessui/react';
// import { AiFillEdit, AiFillDelete } from "react-icons/ai";
// import dayjs from "dayjs";

// const BlogCategory = ({ categories, getCategories, manager, user, page, isLoading }) => {
//   const { t } = useTranslation();
//   const notification = useToastContext();
//   const [editingCategory, setEditingCategory] = useState(null);
//   const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null });
//   const [loadingStates, setLoadingStates] = useState({});

//   const {
//     register,
//     handleSubmit,
//     reset,
//     formState: { errors }
//   } = useForm();

//   const onSubmit = async (data) => {
//     try {
//       setLoadingStates(prev => ({ ...prev, form: true }));
//       let result;
      
//       if (editingCategory) {
//         result = await apiPut(`${apiPath.blogCategory}/${editingCategory._id}`, data);
//       } else {
//         result = await apiPost(apiPath.blogCategory, data);
//       }

//       if (result?.data?.success) {
//         notification.success(result.data.message);
//         reset();
//         setEditingCategory(null);
//         getCategories();
//       } else {
//         notification.error(result?.data?.message);
//       }
//     } catch (error) {
//       console.log('Error:', error);
//       notification.error(error.message);
//     } finally {
//       setLoadingStates(prev => ({ ...prev, form: false }));
//     }
//   };

//   const handleEdit = (category) => {
//     setEditingCategory(category);
//     reset({
//       name: category.name,
//       description: category.description
//     });
//   };

//   const handleCancel = () => {
//     setEditingCategory(null);
//     reset();
//   };

//   const handleDelete = async (item) => {
//     try {
//       setLoadingStates(prev => ({ ...prev, delete: true }));
//       const result = await apiDelete(`${apiPath.blogCategory}/${item._id}`);
      
//       if (result?.data?.success) {
//         notification.success(result.data.message);
//         getCategories();
//       } else {
//         notification.error(result?.data?.message);
//       }
//     } catch (error) {
//       console.log('Error:', error);
//       notification.error(error.message);
//     } finally {
//       setLoadingStates(prev => ({ ...prev, delete: false }));
//       setDeleteModal({ isOpen: false, item: null });
//     }
//   };

//   const handleStatusChange = async (category) => {
//     try {
//       setLoadingStates(prev => ({ ...prev, [category._id]: true }));

//       const payload = {
//         status: category.status === 'active' ? 'inactive' : 'active'
//       };
//       const result = await apiPut(`${apiPath.changeBlogCategoryStatus}/${category._id}`, payload);
      
//       if (result?.data?.success) {
//         notification.success(result.data.message);
//         getCategories();
//       } else {
//         notification.error(result?.data?.message);
//       }
//     } catch (error) {
//       console.log('Error:', error);
//       notification.error(error.message);
//     } finally {
//       setLoadingStates(prev => ({ ...prev, [category._id]: false }));
//     }
//   };

//   return (
//     <div className="p-5">
//       <ConfirmationModal
//         isOpen={deleteModal.isOpen}
//         onClose={() => setDeleteModal({ isOpen: false, item: null })}
//         onConfirm={() => handleDelete(deleteModal.item)}
//         title={t("CONFIRM_DELETE")}
//         message={t("ARE_YOU_SURE_DELETE_THIS_CATEGORY", { name: deleteModal.item?.name })}
//         confirmText={t("DELETE")}
//         cancelText={t("CANCEL")}
//         isLoading={loadingStates.delete}
//         isDanger={true}
//       />

//       {/* Add/Edit Category Form */}
//       <div className="bg-white border border-[#E9EDF9] rounded-lg p-6 mb-6">
//         <h3 className="text-lg font-semibold mb-4">
//           {editingCategory ? t('EDIT_CATEGORY') : t('ADD_CATEGORY')}
//         </h3>
        
//         <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
//           <div className="gap-4">
//             <OInputField
//               wrapperClassName="relative z-0 mb-6 w-full group"
//               name="name"
//               inputLabel={
//                 <>
//                   {t('CATEGORY_NAME')}
//                   <span className="text-red-500">*</span>
//                 </>
//               }
//               type="text"
//               register={register('name', { 
//                 required: 'Category name is required',
//                 minLength: { value: 2, message: 'Category name must be at least 2 characters' }
//               })}
//               errors={errors}
//             />

//             <OTextArea
//               wrapperClassName="relative z-0 mb-6 w-full group"
//               name="description"
//               inputLabel={<>{t('DESCRIPTION')}</>}
//               type="text"
//               register={register('description')}
//             />
//           </div>

//           <div className="flex flex-row-reverse gap-3 space-x-4">
//             <button
//               type="submit"
//               disabled={loadingStates.form}
//               className="bg-LightBlue text-white px-6 py-2 rounded-lg hover:bg-DarkBlue transition duration-150 disabled:opacity-50"
//             >
//               {loadingStates.form ? t('SAVING') : (editingCategory ? t('O_UPDATE') : t('O_ADD'))}
//             </button>
            
//             {editingCategory && (
//               <button
//                 type="button"
//                 onClick={handleCancel}
//                 className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition duration-150"
//               >
//                 {t('O_CANCEL')}
//               </button>
//             )}
//           </div>
//         </form>
//       </div>

//       {/* Categories Table */}
//       <div className="bg-white border border-[#E9EDF9] rounded-lg">
//         <div className="p-4 border-b">
//           <h3 className="text-lg font-semibold">{t('CATEGORIES')}</h3>
//         </div>
        
//         <div className="p-3">
//           <div className="overflow-x-auto relative rounded-lg border">
//             <table className="w-full text-xs text-left text-[#6c6c6c] dark:text-gray-400">
//               <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
//                 <tr>
//                   <th scope="col" className="py-3 px-6">
//                     {t("SR_NO")}
//                   </th>
//                   <th scope="col" className="py-3 px-6">
//                     {t("CATEGORY_NAME")}
//                   </th>
//                   <th scope="col" className="py-3 px-6">
//                     {t("DESCRIPTION")}
//                   </th>
//                   <th scope="col" className="py-3 px-6">
//                     {t("CREATED_DATE")}
//                   </th>
//                   <th scope="col" className="py-3 px-6">
//                     {t("STATUS")}
//                   </th>
//                   <th scope="col" className="py-3 px-6 text-center">
//                     {t("ACTIONS")}
//                   </th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {isLoading ? (
//                   <tr>
//                     <td colSpan="6" className="py-4 px-6 text-center">
//                       {t("LOADING")}...
//                     </td>
//                   </tr>
//                 ) : categories?.length > 0 ? (
//                   categories.map((category, i) => (
//                     <tr
//                       key={category._id}
//                       className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
//                     >
//                       <td className="py-4 px-6 border-r font-medium text-gray-900">
//                         {i + 1 + 10 * (page - 1)}
//                       </td>
//                       <td className="py-4 px-6 border-r text-gray-900">
//                         {category?.name}
//                       </td>
//                       <td className="py-4 px-6 border-r text-gray-900">
//                         {category?.description || 'N/A'}
//                       </td>
//                       <td className="py-4 px-6 border-r text-gray-900">
//                         {dayjs(category?.createdAt).format("MM-DD-YYYY h:mm A")}
//                       </td>
//                       <td className="py-4 px-6 border-r">
//                         <div className="flex items-center">
//                           <Switch
//                             checked={category.status === "active"}
//                             onChange={() => handleStatusChange(category)}
//                             disabled={loadingStates[category._id] || !(user?.role === "admin" || manager?.status)}
//                             className={`${category.status === "active" ? "bg-LightBlue" : "bg-gray-200"
//                               } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-LightBlue ${loadingStates[category._id] ? "opacity-50 cursor-not-allowed" : ""
//                               }`}
//                           >
//                             <span
//                               className={`${category.status === "active" ? "translate-x-6" : "translate-x-1"
//                                 } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
//                             />
//                           </Switch>
//                         </div>
//                       </td>
//                       <td className="text-gray-900 py-4 px-6">
//                         <div className="flex justify-center space-x-3">
//                           {(user?.role === "admin" || manager?.edit) && (
//                             <button
//                               onClick={() => handleEdit(category)}
//                               title={t("EDIT")}
//                               className="text-slate-600 hover:text-indigo-700 transition duration-150"
//                             >
//                               <AiFillEdit className="w-5 h-5" />
//                             </button>
//                           )}
//                           {(user?.role === "admin" || manager?.delete) && (
//                             <button
//                               onClick={() => {
//                                 if (!category?._id) {
//                                   notification.error("Invalid category ID");
//                                   return;
//                                 }
//                                 setDeleteModal({
//                                   isOpen: true,
//                                   item: category
//                                 });
//                               }}
//                               title={t("DELETE")}
//                               className="text-slate-600 hover:text-red-600 transition duration-150"
//                             >
//                               <AiFillDelete className="w-5 h-5" />
//                             </button>
//                           )}
//                         </div>
//                       </td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr className="bg-white border-b text-center">
//                     <td className="py-4 px-6" colSpan={6}>
//                       {t("NO_CATEGORIES_FOUND")}
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default BlogCategory;